import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { FulfillmentStatus } from "@/lib/purchase-request-receiving";

export type RequestItem = { id: string; name: string; nameAr?: string | null; unit: string; supplierName?: string | null; supplierNameAr?: string | null };
export type RequestRecipient = { id: string; name: string };
export type RequestOrderedLine = {
  id: string; poId: string; poNumber: string; itemId: string; itemName: string; itemNameAr?: string | null;
  quantity: string; unit: string; unitCost: string; supplierId: string; supplierName: string;
  deliveryLocationId: string; deliveryDate: string; changeReason: string | null; changed: boolean; changedByName: string | null;
  received?: boolean; receipts: { id: string; receiptNumber: string }[];
};
export type PurchaseRequest = {
  id: string; prNumber: string; locationId: string; locationName: string; locationNameAr?: string | null;
   fulfillmentSourceLocationId?: string | null; fulfillmentSourceName?: string | null; fulfillmentSourceNameAr?: string | null;
  createdByUserId: string; requestedByName: string; createdAt: string; submittedAt?: string | null;
  neededByDate?: string | null; status: "pending_approval" | "open" | "rejected" | "closed" | "cancelled" | "draft";
  approvalRequired: boolean; claimedByName?: string | null; claimedAt?: string | null; teamRoutedAt?: string | null;
  fallbackDueAt?: string | null; fallbackHours?: number | null; routingMode: string; notes?: string | null;
  transferBalanceNote?: string | null; transferExpectedDate?: string | null;
  canApprove: boolean; canOpen: boolean; changed: boolean; received: boolean;
  fulfillmentStatus?: FulfillmentStatus;
  recipients: RequestRecipient[];
  lines: { id: string; itemId: string; itemName: string; itemNameAr?: string | null; quantity: string; unit: string;
    coveredQty?: string; receivedQty?: string; remainingQty?: string; transfers?: { id: string; transferNumber: string; status: string }[];
    declined?: boolean; declineReason?: string | null; changed?: boolean; ordered?: RequestOrderedLine[] }[];
  changeSummary?: { actorName: string | null; changed: number; declined: number; total: number };
  purchaseOrders: { id: string; poNumber: string }[];
  receipts: { id: string; receiptNumber: string }[];
  history: { id: string; type: string; actorName: string; at: string; reason?: string | null; details?: Record<string, unknown> | null }[];
};
export type RequestOptions = {
  locations: { id: string; name: string; nameAr?: string | null }[];
  readLocations?: { id: string; name: string; nameAr?: string | null }[];
  settings: { prManagerApprovalMode: string; prManagerApprovalThreshold: number | string | null; prRoutingMode: string; prFallbackHours: number | null };
  permissions: { create: boolean; process: boolean; viewAll: boolean; approve: boolean };
};
export type CreateRequest = {
  locationId: string; fulfillmentSourceLocationId?: string | null; neededByDate: string; notes: string; sendTo: "team" | "people";
  recipientIds: string[]; lines: { itemId: string; quantity: string; unit: string }[];
};
export type InternalStockRequest = {
  id: string; prNumber: string; destinationLocationId: string; destinationName: string; destinationNameAr?: string | null;
  sourceLocationId: string; sourceName: string; sourceNameAr?: string | null; requesterName: string; neededByDate: string;
  submittedAt: string; notes?: string | null; transferBalanceNote?: string | null; transferExpectedDate?: string | null;
  lines: { requestLineId: string; itemId: string; itemName: string; itemNameAr?: string | null; requestedQty: string; coveredQty: string; remainingQty: string; unit: string }[];
};
export const requestKey = (workspaceId: string) => ["/api/purchase-requests", workspaceId] as const;
export async function requestApi<T>(workspaceId: string, path = "", method = "GET", body?: unknown): Promise<T> {
  const separator = path.includes("?") ? "&" : "?";
  // Explicit workspace scope prevents an in-flight selection change from using another workspace.
  const response = await apiRequest(method, `/api/purchase-requests${path}${separator}workspaceId=${encodeURIComponent(workspaceId)}`, body);
  return response.json();
}
export function useRequestOptions(workspaceId: string, enabled = true) {
  return useQuery<RequestOptions>({ queryKey: [...requestKey(workspaceId), "options"], queryFn: () => requestApi(workspaceId, "/options"), enabled: !!workspaceId && enabled });
}
export function useInternalStockRequests(workspaceId: string, enabled = true) {
  return useQuery<InternalStockRequest[]>({
    queryKey: [...requestKey(workspaceId), "internal-stock-queue"],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/internal-stock-requests?workspaceId=${encodeURIComponent(workspaceId)}`);
      return response.json();
    },
    enabled: !!workspaceId && enabled,
    refetchInterval: 60000,
  });
}
export function useRequestCount(workspaceId: string, enabled: boolean) {
  return useQuery<{ count: number }>({ queryKey: [...requestKey(workspaceId), "count"], queryFn: () => requestApi(workspaceId, "/count"), enabled: !!workspaceId && enabled, refetchInterval: 60000 });
}
export function useRequestList(workspaceId: string, filters: { page: number; pageSize: number; search: string; status: string; locationId: string }, enabled: boolean) {
  const params = new URLSearchParams(Object.entries(filters).map(([key, value]) => [key, String(value)]));
  return useQuery<{ rows: PurchaseRequest[]; total: number; page: number; pageSize: number }>({
    queryKey: [...requestKey(workspaceId), "list", filters], queryFn: () => requestApi(workspaceId, `?${params}`), enabled: !!workspaceId && enabled, refetchInterval: 60000,
  });
}
export function useRequestDetail(workspaceId: string, id: string | null) {
  return useQuery<PurchaseRequest>({
    queryKey: [...requestKey(workspaceId), "detail", id], queryFn: () => requestApi(workspaceId, `/${encodeURIComponent(id!)}`),
    enabled: !!workspaceId && !!id, refetchInterval: 60000,
  });
}
export function useRequestRecipients(workspaceId: string, locationId: string, sourceLocationId?: string, enabled = true) {
  const sourceQuery = sourceLocationId ? `&sourceLocationId=${encodeURIComponent(sourceLocationId)}` : "";
  return useQuery<RequestRecipient[]>({
    queryKey: [...requestKey(workspaceId), "recipients", locationId, sourceLocationId || "procurement"],
    queryFn: () => requestApi(workspaceId, `/recipients?locationId=${encodeURIComponent(locationId)}${sourceQuery}`), enabled: !!workspaceId && !!locationId && enabled && (!sourceLocationId || !!sourceLocationId),
  });
}
export function useInternalRequestSources(workspaceId: string, locationId: string, enabled = true) {
  return useQuery<{ id: string; name: string; nameAr?: string | null }[]>({
    queryKey: [...requestKey(workspaceId), "internal-sources", locationId],
    queryFn: () => requestApi(workspaceId, `/internal-sources?locationId=${encodeURIComponent(locationId)}`),
    enabled: !!workspaceId && !!locationId && enabled,
  });
}
export function useRequestItems(workspaceId: string, locationId: string, query: string) {
  return useQuery<RequestItem[]>({
    queryKey: [...requestKey(workspaceId), "items", locationId, query],
    queryFn: () => requestApi(workspaceId, `/items?q=${encodeURIComponent(query)}&locationId=${encodeURIComponent(locationId)}`),
    enabled: !!workspaceId && !!locationId && !!query.trim(),
  });
}
export function useRequestActions(workspaceId: string) {
  const client = useQueryClient();
  const refresh = (request: PurchaseRequest) => {
    client.setQueryData([...requestKey(workspaceId), "detail", request.id], request);
    void client.invalidateQueries({ queryKey: [...requestKey(workspaceId), "list"] });
    void client.invalidateQueries({ queryKey: [...requestKey(workspaceId), "count"] });
  };
  const create = useMutation({ mutationFn: (body: CreateRequest) => requestApi<PurchaseRequest>(workspaceId, "", "POST", body), onSuccess: refresh });
  const action = useMutation({
    mutationFn: ({ id, action, reason }: { id: string; action: "open" | "approve" | "send-back"; reason?: string }) =>
      requestApi<PurchaseRequest>(workspaceId, `/${encodeURIComponent(id)}/${action}`, "POST", reason ? { reason } : {}),
    onSuccess: refresh,
  });
  return { create, action };
}
